/**
 * Typed HTTP client for the MMS API.
 *
 * Built on `openapi-fetch` with the generated OpenAPI types so every call is
 * checked against the backend contract. The client is same-origin (session
 * cookie auth) and adds the tenant header and CSRF token on every request.
 */
import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";

/** Local-storage key holding the slug of the tenant the user is working in. */
export const TENANT_STORAGE_KEY = "mms.tenant";

const RESERVED_SUBDOMAINS = new Set(["www", "app", "api", "localhost", "127"]);

/**
 * Extract the tenant slug from the current browser hostname if running on a
 * tenant subdomain (e.g. `rsg.mms.local` or `rsg.smartwaters.com`), or `""`.
 */
export function getSubdomainTenantSlug(hostname?: string): string {
  const host = (hostname ?? globalThis.location?.hostname ?? "").toLowerCase();
  if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return "";
  }
  const parts = host.split(".");
  if (parts.length >= 2) {
    const sub = parts[0] ?? "";
    if (sub && !RESERVED_SUBDOMAINS.has(sub)) {
      return sub;
    }
  }
  return "";
}

/** Read the active tenant slug (`?tenant=` query param or subdomain takes precedence over localStorage). */
export function getTenantSlug(): string {
  const subdomain = getSubdomainTenantSlug();
  if (subdomain) {
    return subdomain;
  }
  const search = globalThis.location?.search ?? "";
  if (search) {
    const paramSlug = new URLSearchParams(search).get("tenant")?.trim().toLowerCase() ?? "";
    if (paramSlug) {
      globalThis.localStorage?.setItem(TENANT_STORAGE_KEY, paramSlug);
      return paramSlug;
    }
  }
  return globalThis.localStorage?.getItem(TENANT_STORAGE_KEY) ?? "";
}

/** Persist the active tenant slug for subsequent requests. */
export function setTenantSlug(slug: string): void {
  if (slug) {
    globalThis.localStorage.setItem(TENANT_STORAGE_KEY, slug);
  } else {
    globalThis.localStorage.removeItem(TENANT_STORAGE_KEY);
  }
}

/** Read a cookie value by name from `document.cookie`. */
export function readCookie(name: string): string | null {
  const source = globalThis.document?.cookie ?? "";
  for (const part of source.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) {
      return decodeURIComponent(rest.join("="));
    }
  }
  return null;
}

/** Middleware that attaches tenant and CSRF headers to each request. */
export const contextMiddleware: Middleware = {
  onRequest({ request }) {
    const tenant = getTenantSlug();
    if (tenant && !request.headers.has("X-Tenant")) {
      request.headers.set("X-Tenant", tenant);
    }
    const csrf = readCookie("csrftoken");
    if (csrf && !["GET", "HEAD", "OPTIONS"].includes(request.method)) {
      request.headers.set("X-CSRFToken", csrf);
    }
    return request;
  },
};

/** Same-origin base URL (absolute so `Request` construction works in Node too). */
export const API_BASE_URL: string = globalThis.location?.origin ?? "http://localhost";

/** The shared typed API client. */
export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: "include",
  // Resolve `fetch` lazily so test interceptors (and runtime polyfills) apply.
  fetch: (request) => globalThis.fetch(request),
});
api.use(contextMiddleware);

/** Convenience alias for the generated component schemas. */
export type Schemas = components["schemas"];
